import type {
  SnapCategoryOption,
  SnapClaimOffer,
  SnapExperienceConfiguration,
  SnapPriorityOption,
  SnapEvidenceConstraints
} from '../types';
import { SNAP_BUILD_MARKER, SNAP_PACKAGE_VERSION } from '../types';
import { AnnotationSession } from '../annotation/annotationSession';
import { SnapScreenEditor } from '../editor/snapScreenEditor';
import { AttachmentPicker } from '../attachments/attachmentPicker';
import { safeText } from '../security/sanitize';
import type { ResolvedTheme } from '../theme/theme';
import { applyThemeVars } from '../theme/theme';
import '../styles/snap.css';

export type SnapUiView = 'entry' | 'report' | 'my-requests' | 'request-detail';

export interface SnapUiCallbacks {
  onOpenRequest: () => void;
  onCloseRequest: () => void;
  onOpenReport: () => void;
  onOpenMyRequests: () => void;
  onBackToEntry: () => void;
  onCaptureRequest: () => void;
  onRetakeRequest: () => void;
  onSubmitRequest: () => void;
  onFilesChanged: (files: File[]) => void;
  onSelectRequest: (ticketId: number | string) => void;
  onAddComment: (ticketId: number | string, comment: string) => void;
  /**
   * PR-8.7D.5 — optional account claim. Never required to report or read own requests, so the host
   * treats it as an extra affordance and keeps working when it is absent or refused.
   */
  onClaimAccount?: (email?: string) => void;
  /** Optional conversation refresh for request detail polling. */
  onRefreshDetail?: (ticketId: number | string) => void;
}

export interface SnapRequestListItem {
  ticketId: number | string;
  ticketNumber?: string;
  title?: string;
  status?: string;
  priority?: number | string;
  createdOn?: string;
  categoryName?: string;
  closedOn?: string;
  /** May equal closedOn or createdOn when server has no UpdatedOn. */
  updatedOn?: string;
}

export interface SnapRequestDetailView {
  ticketId: number | string;
  ticketNumber?: string;
  title?: string;
  description?: string;
  status?: string;
  priority?: number | string;
  categoryName?: string;
  createdOn?: string;
  closedOn?: string;
  comments?: Array<{
    commentId?: number | string;
    comment?: string;
    createdOn?: string;
    author?: string;
  }>;
  attachments?: Array<{
    id?: number | string;
    fileName?: string;
    contentType?: string;
    length?: number;
    evidenceKind?: string;
    sourceProducer?: string;
    uploadedOn?: string;
  }>;
  activity?: Array<{
    summary?: string;
    createdOn?: string;
    actor?: string;
  }>;
}

export class SnapUiHost {
  readonly root: HTMLElement;
  private readonly launcher: HTMLButtonElement;
  private readonly backdrop: HTMLDivElement;
  private readonly panel: HTMLElement;
  private readonly heading: HTMLElement;
  private readonly entryView: HTMLElement;
  private readonly reportView: HTMLElement;
  private readonly listView: HTMLElement;
  private readonly detailView: HTMLElement;
  private readonly titleInput: HTMLInputElement;
  private readonly descriptionInput: HTMLTextAreaElement;
  private readonly prioritySelect: HTMLSelectElement;
  private readonly categorySelect: HTMLSelectElement;
  private readonly screenshotDescriptionInput: HTMLTextAreaElement;
  private readonly screenshotDescriptionField: HTMLElement;
  private readonly evidenceHost: HTMLElement;
  private readonly screenEditor: SnapScreenEditor;
  private readonly attachmentPicker: AttachmentPicker;
  private readonly statusEl: HTMLElement;
  private readonly consentEl: HTMLElement;
  private readonly captureBtn: HTMLButtonElement;
  private readonly submitBtn: HTMLButtonElement;
  private readonly myRequestsBtn: HTMLButtonElement;
  private readonly reportBtn: HTMLButtonElement;
  private readonly claimSection: HTMLElement;
  private readonly claimCopy: HTMLElement;
  private readonly claimEmailField: HTMLElement;
  private readonly claimEmailInput: HTMLInputElement;
  private readonly claimBtn: HTMLButtonElement;
  private readonly claimStatus: HTMLElement;
  private readonly listEl: HTMLElement;
  private readonly listFilterInput: HTMLInputElement;
  private readonly detailBody: HTMLElement;
  private readonly commentInput: HTMLTextAreaElement;
  private readonly liveRegion: HTMLElement;
  private readonly liveBadge: HTMLElement;
  private lastFocus: HTMLElement | null = null;
  private unbindKeys: (() => void) | null = null;
  private activeTicketId: number | string | null = null;
  private myRequestsAllowed = true;
  private createAllowed = true;
  private myRequestItems: SnapRequestListItem[] = [];

  constructor(
    mount: HTMLElement,
    theme: ResolvedTheme,
    private readonly callbacks: SnapUiCallbacks,
    options: {
      session: AnnotationSession;
      evidenceConstraints: SnapEvidenceConstraints;
    }
  ) {
    this.root = document.createElement('div');
    this.root.id = 'nv-snap-root';
    this.root.className = 'nv-snap-root';
    this.root.setAttribute('data-no-capture', '1');
    this.root.setAttribute('data-nv-build', SNAP_BUILD_MARKER);
    this.root.setAttribute('data-nv-version', SNAP_PACKAGE_VERSION);
    applyThemeVars(this.root, theme);

    const buildMarker = document.createElement('span');
    buildMarker.className = 'nv-snap-build-marker';
    buildMarker.setAttribute('data-nv-build', SNAP_BUILD_MARKER);
    buildMarker.setAttribute('data-nv-version', SNAP_PACKAGE_VERSION);
    buildMarker.textContent = `${SNAP_BUILD_MARKER}@${SNAP_PACKAGE_VERSION}`;

    this.launcher = document.createElement('button');
    this.launcher.type = 'button';
    this.launcher.className = 'nv-snap-launcher';
    this.launcher.setAttribute('aria-label', 'Open Snap Support');
    this.launcher.textContent = 'Snap';
    this.launcher.addEventListener('click', () => this.callbacks.onOpenRequest());

    this.backdrop = document.createElement('div');
    this.backdrop.className = 'nv-snap-panel-backdrop';
    this.backdrop.hidden = true;
    this.backdrop.addEventListener('click', () => {
      if (this.screenEditor.isExpanded) return;
      this.callbacks.onCloseRequest();
    });

    this.panel = document.createElement('section');
    this.panel.className = 'nv-snap-panel';
    this.panel.hidden = true;
    this.panel.setAttribute('role', 'dialog');
    this.panel.setAttribute('aria-modal', 'true');
    this.panel.setAttribute('aria-labelledby', 'nv-snap-dialog-title');

    this.heading = document.createElement('h2');
    this.heading.id = 'nv-snap-dialog-title';
    this.heading.className = 'nv-snap-title';
    safeText(this.heading, 'Snap Support');

    this.liveBadge = document.createElement('span');
    this.liveBadge.className = 'nv-snap-live-badge';
    this.liveBadge.hidden = true;
    safeText(this.liveBadge, 'Live');
    this.liveBadge.setAttribute('aria-label', 'Conversation updates live');

    this.consentEl = document.createElement('p');
    this.consentEl.className = 'nv-snap-consent';

    this.statusEl = document.createElement('p');
    this.statusEl.className = 'nv-snap-status';
    this.statusEl.setAttribute('role', 'status');
    this.statusEl.setAttribute('aria-live', 'polite');

    this.liveRegion = document.createElement('div');
    this.liveRegion.className = 'nv-snap-sr-only';
    this.liveRegion.setAttribute('aria-live', 'assertive');

    this.entryView = document.createElement('div');
    this.entryView.className = 'nv-snap-view nv-snap-entry';
    const entryCopy = document.createElement('p');
    entryCopy.className = 'nv-snap-muted';
    safeText(
      entryCopy,
      'Capture what is happening and send a support request without leaving this page.'
    );
    this.reportBtn = document.createElement('button');
    this.reportBtn.type = 'button';
    this.reportBtn.className = 'nv-snap-btn nv-snap-btn-primary nv-snap-btn-block';
    safeText(this.reportBtn, 'Report an issue');
    this.reportBtn.addEventListener('click', () => this.callbacks.onOpenReport());
    this.myRequestsBtn = document.createElement('button');
    this.myRequestsBtn.type = 'button';
    this.myRequestsBtn.className = 'nv-snap-btn nv-snap-btn-block';
    safeText(this.myRequestsBtn, 'My Requests');
    this.myRequestsBtn.addEventListener('click', () => this.callbacks.onOpenMyRequests());
    const entryClose = document.createElement('button');
    entryClose.type = 'button';
    entryClose.className = 'nv-snap-btn nv-snap-btn-block';
    safeText(entryClose, 'Close');
    entryClose.addEventListener('click', () => this.callbacks.onCloseRequest());

    // PR-8.7D.5 — claim affordance sits below the primary actions and stays hidden unless the
    // server offered it, so an unclaimed requester never sees a gate on Report or My Requests.
    this.claimSection = document.createElement('div');
    this.claimSection.className = 'nv-snap-claim';
    this.claimSection.hidden = true;
    this.claimCopy = document.createElement('p');
    this.claimCopy.className = 'nv-snap-muted';
    this.claimEmailInput = document.createElement('input');
    this.claimEmailInput.type = 'email';
    this.claimEmailInput.id = 'nv-snap-claim-email';
    this.claimEmailInput.maxLength = 320;
    this.claimEmailInput.setAttribute('aria-label', 'Email address');
    this.claimEmailField = this.wrapField('Email address', this.claimEmailInput);
    this.claimEmailField.hidden = true;
    this.claimBtn = document.createElement('button');
    this.claimBtn.type = 'button';
    this.claimBtn.className = 'nv-snap-btn nv-snap-btn-block';
    safeText(this.claimBtn, 'Claim your support history');
    this.claimBtn.addEventListener('click', () =>
      this.callbacks.onClaimAccount?.(this.claimEmailInput.value.trim() || undefined)
    );
    this.claimStatus = document.createElement('p');
    this.claimStatus.className = 'nv-snap-muted';
    this.claimStatus.hidden = true;
    this.claimSection.append(
      this.claimCopy,
      this.claimEmailField,
      this.claimBtn,
      this.claimStatus
    );

    this.entryView.append(
      entryCopy,
      this.reportBtn,
      this.myRequestsBtn,
      entryClose,
      this.claimSection
    );

    this.titleInput = this.fieldInput('Summary', 'nv-snap-title-input');
    this.descriptionInput = this.fieldTextarea('Details', 'nv-snap-description-input');
    this.prioritySelect = document.createElement('select');
    this.prioritySelect.id = 'nv-snap-priority';
    this.prioritySelect.setAttribute('aria-label', 'Priority');
    this.categorySelect = document.createElement('select');
    this.categorySelect.id = 'nv-snap-category';
    this.categorySelect.setAttribute('aria-label', 'Category');

    this.screenshotDescriptionInput = this.fieldTextarea(
      'Screenshot description',
      'nv-snap-screenshot-description'
    );
    this.screenshotDescriptionInput.rows = 2;
    this.screenshotDescriptionInput.required = false;
    this.screenshotDescriptionField = this.wrapField(
      'Screenshot description',
      this.screenshotDescriptionInput
    );
    this.screenshotDescriptionField.hidden = true;

    this.evidenceHost = document.createElement('div');
    this.evidenceHost.className = 'nv-snap-evidence-host';

    this.screenEditor = new SnapScreenEditor({
      previewMount: this.evidenceHost,
      overlayMount: this.root,
      session: options.session,
      statusTarget: this.statusEl,
      onRetake: () => this.callbacks.onRetakeRequest(),
      onChange: () => this.syncScreenshotDescriptionVisibility()
    });

    const attachmentsMount = document.createElement('div');
    attachmentsMount.className = 'nv-snap-attachments-mount';
    const attachmentsField = document.createElement('div');
    attachmentsField.className = 'nv-snap-field';
    const attachmentsLabel = document.createElement('label');
    attachmentsLabel.htmlFor = 'nv-snap-files';
    safeText(attachmentsLabel, 'Attachments');
    attachmentsField.append(attachmentsLabel, attachmentsMount);

    this.attachmentPicker = new AttachmentPicker({
      mount: attachmentsMount,
      constraints: options.evidenceConstraints,
      inputId: 'nv-snap-files',
      statusTarget: this.statusEl,
      onChange: (files) => this.callbacks.onFilesChanged(files)
    });

    this.captureBtn = this.actionButton('Capture screen', () => this.callbacks.onCaptureRequest());
    this.submitBtn = this.actionButton('Submit request', () => this.callbacks.onSubmitRequest(), true);
    const backBtn = this.actionButton('Back', () => this.callbacks.onBackToEntry());
    const closeBtn = this.actionButton('Close', () => this.callbacks.onCloseRequest());

    const row = document.createElement('div');
    row.className = 'nv-snap-field-row';
    row.append(
      this.wrapField('Priority', this.prioritySelect),
      this.wrapField('Category', this.categorySelect)
    );

    const actions = document.createElement('div');
    actions.className = 'nv-snap-actions nv-snap-actions-sticky';
    actions.append(this.captureBtn, this.submitBtn, backBtn, closeBtn);

    this.reportView = document.createElement('div');
    this.reportView.className = 'nv-snap-view';
    this.reportView.hidden = true;
    this.reportView.append(
      this.consentEl,
      this.wrapField('Summary', this.titleInput),
      this.wrapField('Details', this.descriptionInput),
      row,
      attachmentsField,
      this.evidenceHost,
      this.screenshotDescriptionField,
      actions
    );

    this.listFilterInput = document.createElement('input');
    this.listFilterInput.type = 'search';
    this.listFilterInput.id = 'nv-snap-request-filter';
    this.listFilterInput.className = 'nv-snap-request-filter';
    this.listFilterInput.placeholder = 'Search requests';
    this.listFilterInput.setAttribute('aria-label', 'Search requests');
    this.listFilterInput.addEventListener('input', () => this.renderRequestList());

    this.listView = document.createElement('div');
    this.listView.className = 'nv-snap-view';
    this.listView.hidden = true;
    this.listEl = document.createElement('div');
    this.listEl.className = 'nv-snap-request-list';
    this.listEl.setAttribute('role', 'list');
    const listActions = document.createElement('div');
    listActions.className = 'nv-snap-actions';
    listActions.append(
      this.actionButton('Back', () => this.callbacks.onBackToEntry()),
      this.actionButton('Close', () => this.callbacks.onCloseRequest())
    );
    this.listView.append(
      this.wrapField('Search', this.listFilterInput),
      this.listEl,
      listActions
    );

    this.detailView = document.createElement('div');
    this.detailView.className = 'nv-snap-view';
    this.detailView.hidden = true;
    this.detailBody = document.createElement('div');
    this.detailBody.className = 'nv-snap-request-detail';
    this.commentInput = this.fieldTextarea('Add a message', 'nv-snap-comment');
    this.commentInput.rows = 3;
    this.commentInput.required = false;
    const detailActions = document.createElement('div');
    detailActions.className = 'nv-snap-actions';
    detailActions.append(
      this.actionButton(
        'Send',
        () => {
          if (this.activeTicketId == null) return;
          this.callbacks.onAddComment(this.activeTicketId, this.commentInput.value.trim());
        },
        true
      ),
      this.actionButton('Back to list', () => this.callbacks.onOpenMyRequests()),
      this.actionButton('Close', () => this.callbacks.onCloseRequest())
    );
    this.detailView.append(
      this.liveBadge,
      this.detailBody,
      this.wrapField('Add a message', this.commentInput),
      detailActions
    );

    this.panel.append(
      this.heading,
      this.entryView,
      this.reportView,
      this.listView,
      this.detailView,
      this.statusEl,
      this.liveRegion,
      buildMarker
    );

    this.root.append(this.launcher, this.backdrop, this.panel);
    mount.appendChild(this.root);
  }

  setExperience(experience: SnapExperienceConfiguration): void {
    this.prioritySelect.innerHTML = '';
    const priorities = experience.priorityOptions?.length
      ? experience.priorityOptions
      : ([
          { value: 1, label: 'Critical' },
          { value: 2, label: 'High' },
          { value: 3, label: 'Normal' },
          { value: 4, label: 'Low' }
        ] as SnapPriorityOption[]);
    for (const option of priorities) {
      const el = document.createElement('option');
      el.value = String(option.value);
      el.textContent = option.label;
      if (option.value === (experience.defaultPriority || 3)) el.selected = true;
      this.prioritySelect.appendChild(el);
    }

    this.categorySelect.innerHTML = '';
    const none = document.createElement('option');
    none.value = '';
    none.textContent = '-- None --';
    this.categorySelect.appendChild(none);
    for (const category of experience.categories || ([] as SnapCategoryOption[])) {
      const el = document.createElement('option');
      el.value = String(category.id);
      el.textContent = category.name;
      this.categorySelect.appendChild(el);
    }
  }

  setCapabilities(caps: {
    createAllowed?: boolean;
    myRequestsAllowed?: boolean;
    captureAllowed?: boolean;
  }): void {
    this.createAllowed = caps.createAllowed !== false;
    this.myRequestsAllowed = caps.myRequestsAllowed !== false;
    this.reportBtn.hidden = !this.createAllowed;
    this.myRequestsBtn.hidden = !this.myRequestsAllowed;
    this.setScreenshotEnabled(caps.captureAllowed !== false);
  }

  /**
   * PR-8.7D.5 — render the account-claim offer exactly as the server described it. The offer never
   * carries a claim token, so nothing here can start a claim on its own; the button only asks the
   * server to email the proof.
   */
  setClaimOffer(offer: SnapClaimOffer | null | undefined): void {
    if (!offer?.offered || !this.callbacks.onClaimAccount) {
      this.claimSection.hidden = true;
      return;
    }

    this.claimSection.hidden = false;
    safeText(this.claimBtn, offer.offerLabel || 'Claim your support history');
    safeText(
      this.claimCopy,
      offer.maskedEmail && !offer.requiresEmail
        ? `${offer.offerDescription} We will email ${offer.maskedEmail}.`
        : offer.offerDescription
    );
    this.claimEmailField.hidden = !offer.requiresEmail;
    this.claimStatus.hidden = true;
  }

  setClaimStatus(message: string, tone: 'info' | 'error' | 'success' = 'info'): void {
    this.claimStatus.hidden = !message;
    this.claimStatus.setAttribute('data-tone', tone);
    safeText(this.claimStatus, message);
    safeText(this.liveRegion, message);
  }

  setClaiming(claiming: boolean): void {
    this.claimBtn.disabled = claiming;
    this.claimEmailInput.disabled = claiming;
  }

  getFormValues(): {
    title: string;
    description: string;
    priority: number;
    categoryId: number | null;
    screenshotDescription: string;
  } {
    const categoryRaw = this.categorySelect.value.trim();
    return {
      title: this.titleInput.value.trim(),
      description: this.descriptionInput.value.trim(),
      priority: Number(this.prioritySelect.value || 3),
      categoryId: categoryRaw ? Number(categoryRaw) : null,
      screenshotDescription: this.screenshotDescriptionInput.value.trim()
    };
  }

  getPreviewCanvas(): HTMLCanvasElement {
    return this.screenEditor.previewCanvas;
  }

  isEvidenceEditorOpen(): boolean {
    return this.screenEditor.isExpanded;
  }

  getZoom(): number {
    return this.screenEditor.getZoom();
  }

  getActiveTicketId(): number | string | null {
    return this.activeTicketId;
  }

  setConversationLive(live: boolean): void {
    this.liveBadge.hidden = !live;
  }

  setScreenshotEnabled(enabled: boolean): void {
    this.captureBtn.hidden = !enabled;
    this.captureBtn.disabled = !enabled;
  }

  setConsent(message: string): void {
    safeText(this.consentEl, message);
  }

  setProductLabels(labels: {
    productName?: string;
    reportLabel?: string;
    myRequestsLabel?: string;
  }): void {
    if (labels.productName) safeText(this.heading, labels.productName);
    if (labels.reportLabel) safeText(this.reportBtn, labels.reportLabel);
    if (labels.myRequestsLabel) safeText(this.myRequestsBtn, labels.myRequestsLabel);
  }

  setStatus(message: string, tone: 'info' | 'error' | 'success' = 'info'): void {
    safeText(this.statusEl, message);
    this.statusEl.setAttribute('data-tone', tone);
    safeText(this.liveRegion, message);
  }

  setSubmitting(submitting: boolean): void {
    this.submitBtn.disabled = submitting;
    this.captureBtn.disabled = submitting;
    this.titleInput.disabled = submitting;
    this.descriptionInput.disabled = submitting;
    this.prioritySelect.disabled = submitting;
    this.categorySelect.disabled = submitting;
    this.screenshotDescriptionInput.disabled = submitting;
    this.attachmentPicker.setDisabled(submitting);
    safeText(this.submitBtn, submitting ? 'Submitting…' : 'Submit request');
  }

  showPreview(source: HTMLCanvasElement): void {
    this.screenEditor.setSource(source);
    this.syncScreenshotDescriptionVisibility();
  }

  clearPreview(): void {
    this.screenEditor.clear();
    this.screenshotDescriptionInput.value = '';
    this.syncScreenshotDescriptionVisibility();
  }

  showEntry(): void {
    this.setView('entry');
    this.setConversationLive(false);
    safeText(this.heading, 'Snap Support');
  }

  showReport(): void {
    this.setView('report');
    this.setConversationLive(false);
    safeText(this.heading, 'Report an issue');
    // Clear stale My Requests / submit status so a prior 401 does not linger on Report.
    this.setStatus('', 'info');
    this.titleInput.focus();
  }

  showMyRequestsLoading(): void {
    this.setView('my-requests');
    this.setConversationLive(false);
    safeText(this.heading, 'My Requests');
    this.myRequestItems = [];
    this.listEl.innerHTML = '';
    const loading = document.createElement('p');
    loading.className = 'nv-snap-muted';
    safeText(loading, 'Loading your requests…');
    this.listEl.appendChild(loading);
  }

  showMyRequestsError(message: string): void {
    this.setView('my-requests');
    this.setConversationLive(false);
    safeText(this.heading, 'My Requests');
    this.myRequestItems = [];
    this.listEl.innerHTML = '';
    const error = document.createElement('p');
    error.className = 'nv-snap-muted';
    error.setAttribute('data-tone', 'error');
    safeText(error, message || 'Unable to load your requests.');
    this.listEl.appendChild(error);
  }

  showMyRequests(items: SnapRequestListItem[]): void {
    this.setView('my-requests');
    this.setConversationLive(false);
    safeText(this.heading, 'My Requests');
    this.myRequestItems = items.slice();
    this.renderRequestList();
  }

  showRequestDetail(detail: SnapRequestDetailView): void {
    this.activeTicketId = detail.ticketId;
    this.setView('request-detail');
    safeText(this.heading, detail.ticketNumber || `Request ${detail.ticketId}`);
    this.detailBody.innerHTML = '';

    const bits: Array<[string, string]> = [
      ['Status', detail.status || ''],
      ['Summary', detail.title || ''],
      ['Details', detail.description || ''],
      ['Priority', detail.priority != null && detail.priority !== '' ? String(detail.priority) : ''],
      ['Category', detail.categoryName || ''],
      ['Created', detail.createdOn || ''],
      ['Closed', detail.closedOn || '']
    ];
    for (const [label, value] of bits) {
      if (!value) continue;
      this.detailBody.appendChild(this.detailBlock(label, value));
    }

    if (detail.attachments?.length) {
      const section = document.createElement('div');
      section.className = 'nv-snap-field nv-snap-detail-attachments';
      const title = document.createElement('strong');
      safeText(title, 'Attachments');
      section.appendChild(title);
      const list = document.createElement('ul');
      list.className = 'nv-snap-detail-attachment-list';
      for (const attachment of detail.attachments) {
        const li = document.createElement('li');
        const name = attachment.fileName || 'Attachment';
        const kind = attachment.evidenceKind ? ` · ${attachment.evidenceKind}` : '';
        safeText(li, `${name}${kind}`);
        list.appendChild(li);
      }
      section.appendChild(list);
      this.detailBody.appendChild(section);
    }

    if (detail.activity?.length) {
      const section = document.createElement('div');
      section.className = 'nv-snap-field nv-snap-detail-activity';
      const title = document.createElement('strong');
      safeText(title, 'Activity');
      section.appendChild(title);
      for (const entry of detail.activity) {
        const p = document.createElement('p');
        p.className = 'nv-snap-muted';
        const actorLabel = (entry.actor && entry.actor.trim()) || 'System';
        const when = entry.createdOn ? ` · ${entry.createdOn}` : '';
        safeText(p, `${actorLabel}${when}: ${entry.summary || ''}`);
        section.appendChild(p);
      }
      this.detailBody.appendChild(section);
    }

    const comments = document.createElement('div');
    comments.className = 'nv-snap-comments';
    const header = document.createElement('div');
    header.className = 'nv-snap-comments-header';
    const h = document.createElement('strong');
    safeText(h, 'Conversation');
    const refreshBtn = this.actionButton('Refresh conversation', () => {
      if (this.activeTicketId == null) return;
      if (this.callbacks.onRefreshDetail) {
        this.callbacks.onRefreshDetail(this.activeTicketId);
      } else {
        this.callbacks.onSelectRequest(this.activeTicketId);
      }
    });
    header.append(h, refreshBtn);
    comments.appendChild(header);

    if (detail.comments?.length) {
      for (const comment of detail.comments) {
        const block = document.createElement('div');
        block.className = 'nv-snap-comment';
        const meta = document.createElement('p');
        meta.className = 'nv-snap-muted';
        const author = comment.author || 'Customer';
        const when = comment.createdOn ? ` · ${comment.createdOn}` : '';
        safeText(meta, `${author}${when}`);
        const body = document.createElement('p');
        safeText(body, comment.comment || '');
        block.append(meta, body);
        comments.appendChild(block);
      }
    } else {
      const empty = document.createElement('p');
      empty.className = 'nv-snap-muted';
      safeText(empty, 'No messages yet. Add a comment below.');
      comments.appendChild(empty);
    }
    this.detailBody.appendChild(comments);

    this.commentInput.value = '';
  }

  open(): void {
    this.lastFocus = document.activeElement as HTMLElement | null;
    this.backdrop.hidden = false;
    this.panel.hidden = false;
    this.showEntry();
    this.unbindKeys = this.bindFocusTrap();
  }

  close(): void {
    if (this.screenEditor.isExpanded) this.screenEditor.closeExpanded();
    this.backdrop.hidden = true;
    this.panel.hidden = true;
    this.setConversationLive(false);
    this.unbindKeys?.();
    this.unbindKeys = null;
    const target =
      this.lastFocus && document.contains(this.lastFocus) ? this.lastFocus : this.launcher;
    target.focus();
  }

  setLauncherVisible(visible: boolean): void {
    this.launcher.hidden = !visible;
  }

  destroy(): void {
    this.unbindKeys?.();
    this.unbindKeys = null;
    this.screenEditor.destroy();
    this.attachmentPicker.destroy();
    this.root.remove();
  }

  openEvidenceEditor(): void {
    this.screenEditor.openExpanded();
  }

  closeEvidenceEditor(): void {
    this.screenEditor.closeExpanded();
  }

  private syncScreenshotDescriptionVisibility(): void {
    const hasSource = this.screenEditor.hasSource();
    this.screenshotDescriptionField.hidden = !hasSource;
    this.screenshotDescriptionInput.required = hasSource;
    if (!hasSource) this.screenshotDescriptionInput.value = '';
  }

  private renderRequestList(): void {
    this.listEl.innerHTML = '';
    const items = this.filteredRequestItems();
    if (!this.myRequestItems.length) {
      const empty = document.createElement('p');
      empty.className = 'nv-snap-muted';
      safeText(empty, 'You have no support requests yet.');
      this.listEl.appendChild(empty);
      return;
    }
    if (!items.length) {
      const empty = document.createElement('p');
      empty.className = 'nv-snap-muted';
      safeText(empty, 'No requests match your search.');
      this.listEl.appendChild(empty);
      return;
    }
    for (const item of items) {
      const row = document.createElement('button');
      row.type = 'button';
      row.className = 'nv-snap-request-item';
      row.setAttribute('role', 'listitem');

      const titleLine = document.createElement('div');
      titleLine.className = 'nv-snap-request-item-title';
      safeText(
        titleLine,
        `${item.ticketNumber || item.ticketId} · ${item.title || 'Support request'}`
      );

      const metaLine = document.createElement('div');
      metaLine.className = 'nv-snap-request-item-meta nv-snap-muted';
      const metaParts = [
        item.status || 'Open',
        item.categoryName,
        item.createdOn ? `Created ${item.createdOn}` : '',
        item.closedOn ? `Closed ${item.closedOn}` : '',
        !item.closedOn && item.updatedOn ? `Updated ${item.updatedOn}` : ''
      ].filter(Boolean);
      safeText(metaLine, metaParts.join(' · '));

      row.append(titleLine, metaLine);
      row.addEventListener('click', () => this.callbacks.onSelectRequest(item.ticketId));
      this.listEl.appendChild(row);
    }
  }

  private filteredRequestItems(): SnapRequestListItem[] {
    const q = this.listFilterInput.value.trim().toLowerCase();
    if (!q) return this.myRequestItems;
    return this.myRequestItems.filter((item) => {
      const hay = [
        item.title,
        item.ticketNumber,
        item.status,
        item.categoryName,
        String(item.ticketId)
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }

  private detailBlock(label: string, value: string): HTMLElement {
    const block = document.createElement('div');
    block.className = 'nv-snap-field';
    const title = document.createElement('strong');
    safeText(title, label);
    const body = document.createElement('p');
    safeText(body, value);
    block.append(title, body);
    return block;
  }

  private setView(view: SnapUiView): void {
    this.entryView.hidden = view !== 'entry';
    this.reportView.hidden = view !== 'report';
    this.listView.hidden = view !== 'my-requests';
    this.detailView.hidden = view !== 'request-detail';
  }

  private bindFocusTrap(): () => void {
    const onKey = (evt: KeyboardEvent) => {
      if (evt.key === 'Escape') {
        // SnapScreenEditor owns Escape while expanded.
        if (this.screenEditor.isExpanded) return;
        evt.preventDefault();
        evt.stopPropagation();
        this.callbacks.onCloseRequest();
        return;
      }
      if (this.screenEditor.isExpanded) return;
      if (evt.key !== 'Tab' || this.panel.hidden) return;
      const focusable = this.panel.querySelectorAll<HTMLElement>(
        'button:not([disabled]):not([hidden]), input:not([disabled]):not([hidden]), textarea:not([disabled]):not([hidden]), select:not([disabled]):not([hidden]), [tabindex]:not([tabindex="-1"])'
      );
      const list = Array.from(focusable).filter((el) => !el.hidden && el.offsetParent !== null);
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (evt.shiftKey && document.activeElement === first) {
        evt.preventDefault();
        last.focus();
      } else if (!evt.shiftKey && document.activeElement === last) {
        evt.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }

  private actionButton(label: string, onClick: () => void, primary = false): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = primary ? 'nv-snap-btn nv-snap-btn-primary' : 'nv-snap-btn';
    safeText(btn, label);
    btn.addEventListener('click', onClick);
    return btn;
  }

  private fieldInput(label: string, id: string): HTMLInputElement {
    const input = document.createElement('input');
    input.type = 'text';
    input.id = id;
    input.required = true;
    input.maxLength = 200;
    input.setAttribute('aria-label', label);
    return input;
  }

  private fieldTextarea(label: string, id: string): HTMLTextAreaElement {
    const input = document.createElement('textarea');
    input.id = id;
    input.rows = 4;
    input.required = true;
    input.maxLength = 4000;
    input.setAttribute('aria-label', label);
    return input;
  }

  private wrapField(labelText: string, control: HTMLElement): HTMLElement {
    const wrap = document.createElement('div');
    wrap.className = 'nv-snap-field';
    const label = document.createElement('label');
    label.htmlFor = control.id;
    safeText(label, labelText);
    wrap.append(label, control);
    return wrap;
  }
}
